import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DefaultCustomRatePayload,
  DefaultCustomRateResponse,
  ProductsRateResponse
} from '../../../store/_declarations/financialCalculator';
import { postDefaultCustomRate } from '../../../store/api/financialCalculator';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setPromoCodeValidation } from '../../../store/slices/appSlice';
import bbCodeParsed from '../../../utils/bbCodeParsed';
import { formatNumber } from '../../../utils/formatters';
import {
  decimalsSeparator,
  isVatInluded,
  thousandsSeparator
} from '../../../utils/marketSpecs';
import { checkVisibilityRule } from '../../../utils/parsers';
import ErrorHandler from '../../atoms/ErrorHandler';
import Font from '../../atoms/Font';
import Icon from '../../atoms/Icon';
import SliderInput from '../../atoms/SliderInput';
import Spinner from '../../atoms/Spinner';
import OptionalServices from '../../molecules/OptionalServices';
import PromoCode from '../../molecules/PromoCode';
import ServiceList from '../ServicesList';
import {
  ControlsWrapper,
  Divider,
  ErrorWrapper,
  HeaderProduct,
  SelectedCardContainer,
  WrapperControlsTax,
  WrapperProduct
} from './styles';

export type ProductCardProps = {
  isLoading?: boolean;
  item: ProductsRateResponse.PackageSelection;
  selected: boolean;
};

const predefinedOrderServiceList = [
  '1701002807',
  '1701002308',
  '1701003439',
  '1701004097',
  '1701002630',
  '1701002631',
  '1701002682',
  '1701002684',
  '1701003277',
  '1701003282',
  '1701004208',
  '1701004168',
  '1701002658',
  '1701002659',
  '1701002660',
  '1701002661',
  '1701003707',
  '1701003708',
  '1701003780',
  '1701004027',
  '1701002123',
  '1701001863',
  '1701000941',
  '1701000942',
  '1701000943',
  '1701000944',
  '1701000945',
  '1701001214',
  '1701001219',
  '1701001979',
  '1701002911',
  '1701002707',
  '1701002708',
  '1701002709',
  '1701003702',
  '1701003947',
  '1701003948',
  '1701003949',
  '1701003950',
  '1701003951',
  '1701003952',
  '1701003953',
  '1701003954',
  '1701003955',
  '1701003956',
  '1701003957',
  '1701003958',
  '1701002849',
  '1701002851',
  '1701002499',
  '1701004087',
  '1701004088',
  '1701004089',
  '1701004090',
  '1701004091',
  '1701004092',
  '1701004093',
  '1701004094',
  '1701004095',
  '1701003997',
  '1701003998',
  '1701003999',
  '1701001443',
  '1701001123',
  '1701001453',
  '1701001448',
  '1701001437'
];

type itemDetailsProps = {
  legalMention: string;
  monthlyPrice: string;
  monthlyPriceVat: string;
  productName: string;
  servicesList: {
    description: string;
    name: string;
  }[];
};

const sortingServices = (
  a: DefaultCustomRateResponse.Service,
  b: DefaultCustomRateResponse.Service
) => {
  const indexA = predefinedOrderServiceList.indexOf(a.serviceCode);
  const indexB = predefinedOrderServiceList.indexOf(b.serviceCode);

  if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB;
  }
  if (indexA !== -1) {
    return -1;
  }
  if (indexB !== -1) {
    return 1;
  }
  return a.label.localeCompare(b.label);
};

const getNewGlobalMileageForNewDuration = (
  previousGlobalMileage?: number,
  actualDuration?: number,
  newDuration?: number
) => {
  try {
    if (!previousGlobalMileage || !actualDuration || !newDuration) {
      throw new Error('Invalid parameters');
    }
    const montlyMileage = previousGlobalMileage / actualDuration;
    return montlyMileage * newDuration;
  } catch {
    return 0;
  }
};

const getNewGlobalMileage = (duration?: number, annualMileage?: number) => {
  try {
    if (!duration || !annualMileage) {
      throw new Error('Invalid parameters');
    }

    return (annualMileage / 12) * duration;
  } catch {
    return 0;
  }
};

let downpaymentsWithoutVat: number[] = [];
let downpaymentsWithVat: number[] = [];

const createArraysDownpayment = (initalDownPayment: {
  b2c: boolean;
  deposit: number;
  depositWithVat: number;
}) => {
  try {
    const { b2c, deposit = 0, depositWithVat = 0 } = initalDownPayment;
    const vatPercentage = Math.round(
      ((depositWithVat - deposit) / deposit) * 100
    );

    if (downpaymentsWithoutVat.length > 0) {
      downpaymentsWithoutVat = [];
    }
    if (downpaymentsWithVat.length > 0) {
      downpaymentsWithVat = [];
    }

    if (b2c && depositWithVat) {
      for (let step = 0; step < depositWithVat; step += 100) {
        downpaymentsWithVat.push(step);
      }
      downpaymentsWithVat.push(depositWithVat);

      downpaymentsWithVat.forEach((value) => {
        downpaymentsWithoutVat.push(
          Number((value / (1 + vatPercentage / 100)).toFixed(2))
        );
      });
    }

    if (!b2c && deposit) {
      for (let step = 0; step < deposit; step += 100) {
        downpaymentsWithoutVat.push(step);
      }
      downpaymentsWithoutVat.push(deposit);

      downpaymentsWithoutVat.forEach((value) => {
        downpaymentsWithVat.push(
          Number((value * (1 + vatPercentage / 100)).toFixed(2))
        );
      });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error creating downpayment arrays', e);
  }
};

const getAnnualMileage = (
  totalKmContract: number,
  contractDuration: number
) => {
  try {
    return Math.round((totalKmContract / contractDuration) * 12);
  } catch {
    return 0;
  }
};

const ProductCard: FC<ProductCardProps> = ({ isLoading, item, selected }) => {
  const { t } = useTranslation();
  const {
    consumerPayload,
    initalDownPayment,
    promoCodeValidation,
    visibilityFeatures,
    widgetType
  } = useAppSelector((state) => state.app);
  const { invalidManufacturerCode, productsRate } = useAppSelector(
    (state) => state.financialCalculator
  );
  const [selectedCard, setSelectedCard] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const [itemDetails, setItemDetails] = useState<itemDetailsProps>({
    legalMention: '',
    monthlyPrice: '',
    monthlyPriceVat: '',
    productName: '',
    servicesList: []
  });
  const { status } = useAppSelector((state) => state.financialCalculator);
  const [currentParameters, setCurrentParameters] =
    useState<DefaultCustomRatePayload.Parameters>();
  const [payloadParameters, setPayloadParameters] =
    useState<DefaultCustomRatePayload.Parameters>();
  const defaultCustomRateError = status.postDefaultCustomRate === 'failed';
  const withVat = isVatInluded(
    consumerPayload?.vehicle,
    consumerPayload?.context.customer,
    consumerPayload?.context.countryCode
  );

  const responseData = (productsRate?.packageSelection || []).find(
    (i) => i.uuid === item.uuid
  )?.result?.teaser?.data;

  const formatterAmount = useCallback(
    (value: number) =>
      formatNumber(value, {
        decimalsSeparator: decimalsSeparator(
          consumerPayload?.context.countryCode
        ),
        hideDecimals: true,
        tousandSeparator: thousandsSeparator(
          consumerPayload?.context.countryCode
        )
      }),
    [consumerPayload?.context.countryCode]
  );

  useEffect(() => {
    if (initalDownPayment) {
      createArraysDownpayment({ b2c: withVat, ...initalDownPayment });
    }
  }, [initalDownPayment]);

  useEffect(() => {
    if (item) {
      const { brandLabel, longLabel } =
        consumerPayload?.vehicle?.presentation || {};

      const legalMention = (item?.result?.globalText?.legalMention || [])
        .map(({ text }: DefaultCustomRateResponse.LegalMention) =>
          bbCodeParsed(text, brandLabel, longLabel)
        )
        .join('<br />');

      const formatterSettings = {
        decimalsSeparator: decimalsSeparator(
          consumerPayload?.context.countryCode
        ),
        hideDecimals: false,
        roundDecimals: true,
        tousandSeparator: thousandsSeparator(
          consumerPayload?.context.countryCode
        )
      };

      setItemDetails({
        legalMention,
        monthlyPrice: `${formatNumber(
          item?.result?.teaser?.regularPayment?.amountExclTax || 0,
          formatterSettings
        )} ${t('Pricing')}`,
        monthlyPriceVat: `${formatNumber(
          item?.result?.teaser?.regularPayment?.amountInclTax || 0,
          formatterSettings
        )} ${t('Pricing')}`,
        productName: item?.result?.teaser?.data?.financialProductLabel || '',
        servicesList: (item?.result?.services || [])
          ?.filter((e) => e.label)
          ?.sort(sortingServices)
          ?.map((ser) => ({
            description: ser?.informations
              ?.map((i) => i.text)
              .join(',')
              .replaceAll(' - ', '<br/>- ') as string,
            name: ser.label
          }))
      });
    }
  }, [item]);

  useEffect(() => {
    if (selected) {
      setSelectedCard(true);
    } else {
      setSelectedCard(false);
    }
  }, [selected]);

  useEffect(() => {
    const parseToIntOrUndefined = (value: any): number | undefined =>
      value ? parseInt(value, 10) : undefined;

    const getCashDeposit = (): number | undefined => {
      const countryCode = consumerPayload?.context.countryCode?.toUpperCase();

      if (countryCode === 'UK') {
        return undefined;
      }

      const arrayValues = withVat
        ? downpaymentsWithVat
        : downpaymentsWithoutVat;
      const cashDeposit = consumerPayload?.parameters?.cashDeposit
        ? arrayValues[
            downpaymentsWithoutVat.indexOf(
              parseFloat(String(consumerPayload?.parameters?.cashDeposit))
            )
          ]
        : 0;
      return parseToIntOrUndefined(cashDeposit);
    };

    if (consumerPayload?.parameters) {
      const { duration, globalMileage, numberOfInitialRentals } =
        consumerPayload.parameters;

      setCurrentParameters({
        cashDeposit: getCashDeposit(),
        duration: parseToIntOrUndefined(duration),
        globalMileage: parseToIntOrUndefined(globalMileage),
        numberOfInitialRentals: parseToIntOrUndefined(numberOfInitialRentals)
      });
    } else {
      const deposit = withVat
        ? responseData?.depositWithVat
        : responseData?.deposit;

      setCurrentParameters({
        cashDeposit: deposit,
        duration: responseData?.duration,
        globalMileage: responseData?.globalMileage,
        numberOfInitialRentals: responseData?.numberOfInitialRentals
      });
    }
  }, [consumerPayload]);

  const handleOnSelect = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setSelectedCard(true);
  };

  const handleUpdateParams = (
    name: keyof DefaultCustomRatePayload.Parameters,
    value: number
  ) => {
    let newValues = { [name]: value };
    if (currentParameters && name === 'globalMileage') {
      newValues = {
        globalMileage: getNewGlobalMileage(currentParameters.duration, value)
      };
    }
    if (currentParameters && name === 'duration') {
      newValues = {
        duration: value,
        globalMileage: getNewGlobalMileageForNewDuration(
          currentParameters.globalMileage,
          currentParameters.duration,
          value
        )
      };
    }

    setCurrentParameters({
      ...(consumerPayload?.parameters || {}),
      ...currentParameters,
      ...newValues
    });
  };

  const handleSliderChange = (
    name: keyof DefaultCustomRatePayload.Parameters,
    value: number
  ) => {
    if (consumerPayload) {
      dispatch(setPromoCodeValidation(false));

      const arrayValues = withVat
        ? downpaymentsWithVat
        : downpaymentsWithoutVat;

      const cashDeposit =
        downpaymentsWithoutVat[
          arrayValues.indexOf(currentParameters?.cashDeposit || 0)
        ];

      const parameters: any = {
        ...currentParameters,
        cashDeposit
      };

      switch (name) {
        case 'duration':
          parameters.globalMileage =
            (parameters.globalMileage / (parameters.duration / 12)) *
            (value / 12);
          parameters.duration = value;
          break;
        case 'cashDeposit':
          parameters.cashDeposit =
            downpaymentsWithoutVat[
              withVat
                ? downpaymentsWithVat.indexOf(value)
                : downpaymentsWithoutVat.indexOf(value)
            ];
          break;
        case 'globalMileage':
          parameters.globalMileage = (parameters.duration / 12) * value;
          break;
        default:
          parameters[name] = value;
          break;
      }
      dispatch(
        postDefaultCustomRate({
          ...consumerPayload,
          context: {
            ...consumerPayload.context,
            discountCode: item.result.teaser.regularPayment.discountCode,
            quotationID: item.result.quotationID
          },
          parameters: {
            ...consumerPayload.parameters,
            ...parameters,
            productKey: item.result.teaser.data.productKey
          },
          uuid: item.uuid
        })
      );
      setPayloadParameters({
        ...consumerPayload.parameters,
        ...parameters,
        productKey: item.result.teaser.data.productKey
      });
      handleUpdateParams(name, value);
    }
  };

  let content;

  if (isLoading) {
    content = <Spinner size={16} testid="product-cart-spinner" />;
  } else if (widgetType === 'modal') {
    content = `${withVat ? itemDetails.monthlyPriceVat : itemDetails.monthlyPrice}${consumerPayload?.context.countryCode?.toUpperCase() === 'ES' ? '' : '*'}`;
  } else {
    content = null;
  }

  return (
    <WrapperProduct
      $selected={selectedCard}
      $type="1"
      $widgetType={widgetType}
      data-testid="wrapper_product"
      onClick={widgetType === 'modal' ? handleOnSelect : undefined}
    >
      {widgetType === 'modal' && (
        <HeaderProduct>
          <Font data-testid="product-name" variant="heading4">
            {t('ProductName')}
          </Font>
          <Font
            customConfiguration={{ color: 'brand1', textAlign: 'right' }}
            data-testid="monthlyPrice"
            variant="heading4"
          >
            {content}
          </Font>
        </HeaderProduct>
      )}
      <div>
        <WrapperControlsTax>
          <ControlsWrapper
            $widgetType={widgetType}
            data-testid="controls-wrapper"
          >
            {consumerPayload?.context.countryCode === 'UK' ? (
              <SliderInput
                buttonName={`${t('InitialRentals')}`}
                initialValue={currentParameters?.numberOfInitialRentals}
                isUnitMeasureVisible
                labelName={`${t('InitialRentals')}`}
                onChange={(value) =>
                  handleSliderChange('numberOfInitialRentals', value)
                }
                tickmarks={item?.criteriaAdjustment?.deposits || []}
                unitMeasure={`${t('UnitMeasureMonths')}`}
              />
            ) : (
              <SliderInput
                buttonName={`${t('Downpayment')}`}
                extraText={
                  consumerPayload?.context.countryCode?.toUpperCase() === 'ES'
                    ? ''
                    : '*'
                }
                formatter={formatterAmount}
                hideTickmarks
                initialValue={currentParameters?.cashDeposit}
                isUnitMeasureVisible
                labelName={`${t('Downpayment')}`}
                onChange={(value) => handleSliderChange('cashDeposit', value)}
                tickmarks={
                  withVat ? downpaymentsWithVat : downpaymentsWithoutVat
                }
                unitMeasure={`${t('Currency')}`}
              />
            )}
            <SliderInput
              buttonName={`${t('Duration')}`}
              initialValue={currentParameters?.duration}
              isUnitMeasureVisible
              labelName={`${t('Duration')}`}
              onChange={(value) => handleSliderChange('duration', value)}
              tickmarks={item?.criteriaAdjustment?.durations || []}
              unitMeasure={`${t('UnitMeasureMonths')}`}
            />
            <SliderInput
              buttonName={`${t('AnnualMilage')}`}
              formatter={formatterAmount}
              initialValue={getAnnualMileage(
                currentParameters?.globalMileage || 0,
                currentParameters?.duration || 0
              )}
              isUnitMeasureVisible
              labelName={`${t('AnnualMilage')}`}
              onChange={(value) => handleSliderChange('globalMileage', value)}
              tickmarks={item?.criteriaAdjustment?.annualMileages || []}
              unitMeasure={`${t('UnitMeasureKm')}`}
            />
          </ControlsWrapper>
          {!['UK', 'ES'].includes(
            consumerPayload?.context.countryCode?.toUpperCase() || ''
          ) && (
            <Font
              customConfiguration={{ color: 'neutral400' }}
              variant="bodySBold"
            >
              *{withVat ? t('VATinc') : t('VATexcl')}
            </Font>
          )}
        </WrapperControlsTax>

        <ErrorWrapper $widgetType={widgetType}>
          {!promoCodeValidation && (
            <>
              {defaultCustomRateError && (
                <ErrorHandler
                  currentParameters={currentParameters}
                  data-testid="error-handler"
                  error={
                    invalidManufacturerCode
                      ? t('ManufacturerCodeIncorrectMsg')
                      : t('ErrorMessage')
                  }
                />
              )}
              {isLoading && widgetType === 'embedded' && (
                <Spinner data-testid="product-cart-spinner" size={24} />
              )}
            </>
          )}
        </ErrorWrapper>

        <ServiceList servicesList={itemDetails.servicesList} />
        {checkVisibilityRule('optionalServices', visibilityFeatures) && (
          <OptionalServices />
        )}
      </div>

      {widgetType === 'modal' && (
        <>
          <Divider />
          {itemDetails.productName && (
            <Font
              customConfiguration={{ color: 'textBody' }}
              dangerouslySetInnerHTML={{ __html: itemDetails.legalMention }}
              variant="bodySRegular"
            />
          )}
          {selectedCard && (
            <SelectedCardContainer data-testid="icon_selected">
              <Icon iconColor="white" iconName="InterfaceCheck" />
            </SelectedCardContainer>
          )}
        </>
      )}

      {checkVisibilityRule('promoCode', visibilityFeatures) &&
        consumerPayload?.context?.countryCode !== 'UK' &&
        consumerPayload?.context?.siteCode === 'ECOMMERCE' && (
          <PromoCode
            isLoading={isLoading}
            item={item}
            parameters={payloadParameters}
          />
        )}
    </WrapperProduct>
  );
};

export default ProductCard;
